import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const API = "/api";
const SERVICES = ["Web Design & Engineering", "Brand & Visual Systems", "Conversion Funnel", "Custom Backend", "Not sure yet"];
const BUDGETS = ["$500 – $1k", "$1k – $1.5k", "$1.5k – $2.5k", "$2,500+"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", service: SERVICES[0], budget: BUDGETS[1], details: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Transmission received. We'll respond within 24 hours.");
      setForm({ name: "", email: "", service: SERVICES[0], budget: BUDGETS[1], details: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transmission failed. Try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-40 pb-28">
      <Seo
        title="Start a Project — Fredline Studios"
        siteName="Fredline Studios"
        description="Tell Fredline Studios about your project. Bespoke websites, brand systems and conversion funnels — scoped, priced and built fast."
      />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <span className="mono-label">START A PROJECT</span>
            <h1 className="font-display metal-text mt-4 text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
              LET'S FORGE SOMETHING.
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
              Brief us in two minutes. You'll get a real reply from the people who will actually build your site — within one business day.
            </p>

            <div className="mt-12 space-y-6 border-t border-[#1E2335] pt-10">
              <div>
                <span className="mono-label text-slate-500">DIRECT LINE</span>
                <a
                  href="mailto:pfred@fredlinestudios.com"
                  data-testid="contact-email-link"
                  className="font-display mt-1 block text-xl font-bold text-slate-100 transition-colors duration-200 hover:text-cyan-300"
                >
                  pfred@fredlinestudios.com
                </a>
              </div>
              <div>
                <span className="mono-label text-slate-500">RESPONSE TIME</span>
                <p className="mt-1 text-sm text-slate-300">Under 24 hours, Monday to Friday.</p>
              </div>
              <div>
                <span className="mono-label text-slate-500">CURRENT AVAILABILITY</span>
                <p className="mt-1 flex items-center gap-2 text-sm text-slate-300">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-400" />
                  Booking new projects now
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="chamfer-sm bg-gradient-to-br from-[#2A3045] to-[#1E2335] p-px"
            >
              <div className="chamfer-sm space-y-6 bg-[#0D0E15] p-8 sm:p-10">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mono-label mb-2 block text-slate-500">Name</label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      className="field"
                      placeholder="Your name"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={set("name")}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mono-label mb-2 block text-slate-500">Email</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      className="field"
                      placeholder="you@company.com"
                      required
                      value={form.email}
                      onChange={set("email")}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-service" className="mono-label mb-2 block text-slate-500">What do you need?</label>
                  <select
                    id="contact-service"
                    data-testid="contact-service-select"
                    className="field"
                    value={form.service}
                    onChange={set("service")}
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <span className="mono-label mb-3 block text-slate-500">Budget range</span>
                  <div className="flex flex-wrap gap-2">
                    {BUDGETS.map((b) => (
                      <button
                        key={b}
                        type="button"
                        data-testid={`contact-budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        onClick={() => setForm((f) => ({ ...f, budget: b }))}
                        className={`border px-4 py-2.5 font-mono text-[12px] tracking-wider transition-all duration-200 ${
                          form.budget === b
                            ? "border-cyan-400 bg-cyan-400/10 text-cyan-300 shadow-[0_0_16px_rgba(0,240,255,0.2)]"
                            : "border-[#1E2335] text-slate-400 hover:border-[#2A3045] hover:text-slate-200"
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-details" className="mono-label mb-2 block text-slate-500">Project details</label>
                  <textarea
                    id="contact-details"
                    data-testid="contact-details-textarea"
                    className="field min-h-32 resize-y"
                    placeholder="What are you building, who is it for, and when do you need it?"
                    required
                    minLength={10}
                    value={form.details}
                    onChange={set("details")}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={sending}
                  className="group inline-flex w-full items-center justify-center gap-2 bg-cyan-400 px-8 py-4 font-mono text-[12px] font-bold tracking-[0.2em] text-[#07080C] uppercase transition-all duration-200 hover:bg-cyan-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)] active:scale-[0.98] disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>
                      Transmit Brief
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
